import { useCallback, useRef, useState } from "react";

import {
	AppstoreOutlined,
	CalendarOutlined,
	CloseCircleOutlined,
	LeftOutlined,
	PlusOutlined,
	RightOutlined,
	SaveOutlined,
} from "@ant-design/icons";
import dayGridPlugin from "@fullcalendar/daygrid";
import interactionPlugin from "@fullcalendar/interaction";
import FullCalendarComponent from "@fullcalendar/react";
import timeGridPlugin from "@fullcalendar/timegrid";
import { Button, Drawer, Flex, Select, Space, Typography, message } from "antd";

import { buildApiUrl, fetchAPI, useAPI } from "@neu/libui";

import { FormDraw } from "#components";
import type { FormDrawHandle } from "#components";

import styles from "./Schedule.module.scss";

import type {
	DateSelectArg,
	DatesSetArg,
	EventClickArg,
	EventDropArg,
} from "@fullcalendar/core";
import type { EventResizeDoneArg } from "@fullcalendar/interaction";

// Workaround for React 19 JSX type compatibility
const FullCalendar = FullCalendarComponent as any;
const { Text, Title } = Typography;

interface CalendarHeaderProps {
	calRef: React.RefObject<FullCalendarComponent | null>;
	currentDate: Date;
	currentView: string;
	onViewChange: (view: string) => void;
	onDateChange: (date: Date) => void;
	onAddEvent: () => void;
}

const formatDateTitle = (date: Date) =>
	date.toLocaleDateString("en-US", {
		month: "long",
		day: "numeric",
		year: "numeric",
	});

const formatDateRange = (date: Date, view: string) => {
	const start = new Date(date);
	const end = new Date(date);

	if (view === "dayGridMonth") {
		start.setDate(1);
		end.setMonth(end.getMonth() + 1);
		end.setDate(0);
	} else if (view === "timeGridWeek") {
		const day = start.getDay();
		start.setDate(start.getDate() - day);
		end.setDate(start.getDate() + 6);
	}

	const formatOptions: Intl.DateTimeFormatOptions = {
		month: "short",
		day: "numeric",
		year: "numeric",
	};
	return `${start.toLocaleDateString("en-US", formatOptions)} - ${end.toLocaleDateString("en-US", formatOptions)}`;
};

const CalendarHeader = ({
	calRef,
	currentDate,
	currentView,
	onViewChange,
	onDateChange,
	onAddEvent,
}: CalendarHeaderProps) => {
	const handlePrev = () => calRef.current?.getApi().prev();
	const handleNext = () => calRef.current?.getApi().next();
	const handleToday = () => {
		calRef.current?.getApi().today();
		onDateChange(new Date());
	};

	const handleViewChange = (view: string) => {
		calRef.current?.getApi().changeView(view);
		onViewChange(view);
	};

	const viewOptions = [
		{ value: "dayGridMonth", label: "Month" },
		{ value: "timeGridWeek", label: "Week" },
		{ value: "timeGridDay", label: "Day" },
	];

	return (
		<Flex vertical gap={16} className={styles.headerWrapper}>
			<Flex
				align="center"
				justify="space-between"
				className={styles.calendarHeader}
			>
				<Flex vertical>
					<Title level={5} className={styles.dateTitle}>
						{formatDateTitle(currentDate)}
					</Title>
					<Text type="secondary" className={styles.dateRange}>
						{formatDateRange(currentDate, currentView)}
					</Text>
				</Flex>

				<Flex align="center" gap={12}>
					<Flex className={styles.viewSwitcher} align="center">
						{viewOptions.map((view) => (
							<Button
								key={view.value}
								className={`${styles.viewBtn} ${currentView === view.value ? styles.activeView : ""}`}
								onClick={() => {
									handleViewChange(view.value);
								}}
							>
								<Flex align="center" gap={8} justify="center">
									{currentView === view.value && (
										<LeftOutlined
											className={styles.internalNavIcon}
											onClick={(e: React.MouseEvent) => {
												e.stopPropagation();
												handlePrev();
											}}
										/>
									)}
									<span>{view.label}</span>
									{currentView === view.value && (
										<RightOutlined
											className={styles.internalNavIcon}
											onClick={(e: React.MouseEvent) => {
												e.stopPropagation();
												handleNext();
											}}
										/>
									)}
								</Flex>
							</Button>
						))}
					</Flex>

					<Button onClick={handleToday}>Today</Button>
				</Flex>

				<Button
					type="primary"
					icon={<CalendarOutlined />}
					onClick={onAddEvent}
				>
					ADD EVENT
				</Button>
			</Flex>
		</Flex>
	);
};
// Interface for FullCalendar event format
interface CalendarEvent {
	id: string;
	title: string;
	start: string;
	end: string;
	allDay?: boolean;
	backgroundColor?: string;
	borderColor?: string;
}

// Interface for API response
interface ScheduleEventResponse {
	id: number;
	title: string;
	is_all_day: boolean;
	start_date: string;
	end_date: string;
	color?: string;
	recurrence_group_id?: string;
}

export const Schedule = () => {
	const calRef = useRef<FullCalendarComponent>(null);
	const formHandleRef = useRef<FormDrawHandle | null>(null);
	const [currentDate, setCurrentDate] = useState(new Date());
	const [currentView, setCurrentView] = useState("dayGridMonth");
	const [drawerOpen, setDrawerOpen] = useState(false);
	const [selectedDate, setSelectedDate] = useState<Date | null>(null);
	const [dateRange, setDateRange] = useState<{ start: string; end: string }>({
		start: new Date().toISOString(),
		end: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString(),
	});

	// Fetch events from API
	const [{ data: eventsData }, eventsActions] = useAPI(
		`/schedule-events?start_date=${encodeURIComponent(dateRange.start)}&end_date=${encodeURIComponent(dateRange.end)}`,
		{ enabled: true }
	);

	// Transform API events to FullCalendar format
	const calendarEvents: CalendarEvent[] = (eventsData?.data || []).map(
		(event: ScheduleEventResponse) => ({
			id: String(event.id),
			title: event.title,
			start: event.start_date,
			end: event.end_date,
			allDay: event.is_all_day,
			backgroundColor: event.color || "#3b82f6",
			borderColor: event.color || "#3b82f6",
		})
	);

	const handleDatesSet = useCallback((arg: DatesSetArg) => {
		setCurrentDate(arg.start);
		setCurrentView(arg.view.type);
		// Update date range for fetching events
		setDateRange({
			start: arg.start.toISOString(),
			end: arg.end.toISOString(),
		});
	}, []);

	// Open drawer for new event
	const handleAddEvent = useCallback(() => {
		setSelectedDate(null);
		setDrawerOpen(true);
	}, []);

	// Handle calendar date/time selection
	const handleDateSelect = useCallback((selectInfo: DateSelectArg) => {
		setSelectedDate(selectInfo.start);
		setDrawerOpen(true);
		// Clear the selection
		selectInfo.view.calendar.unselect();
	}, []);

	// Handle click on existing event
	const handleEventClick = useCallback((_clickInfo: EventClickArg) => {
		// TODO: Implement event editing - open drawer in edit mode
		// For now, this is a placeholder for future edit functionality
		void _clickInfo.event.id;
	}, []);

	// Close drawer
	const handleDrawerClose = useCallback(() => {
		setDrawerOpen(false);
		setSelectedDate(null);
	}, []);

	// Handle form ready
	const handleFormReady = useCallback((handle: FormDrawHandle) => {
		formHandleRef.current = handle;
	}, []);

	// Handle save button click
	const handleSave = useCallback(async () => {
		if (formHandleRef.current) {
			await formHandleRef.current.submit();
			setDrawerOpen(false);
			setSelectedDate(null);
			// Refresh events after save
			await eventsActions.refresh();
		}
	}, [eventsActions]);

	// Handle event drag or resize (both update start_date and end_date)
	const handleEventChange = useCallback(
		async (info: EventDropArg | EventResizeDoneArg) => {
			const { event, revert } = info;
			const eventId = event.id;

			if (!event.start) {
				revert();
				return;
			}

			try {
				await fetchAPI(buildApiUrl(`/schedule-events/${eventId}`), {
					method: "PUT",
					body: JSON.stringify({
						start_date: event.start.toISOString(),
						end_date:
							event.end?.toISOString() ??
							event.start.toISOString(),
					}),
				});
				message.success("Event updated successfully");
			} catch (error) {
				message.error(
					error instanceof Error
						? error.message
						: "Failed to update event"
				);
				revert();
			}
		},
		[]
	);

	// Build initial values for the form when a date is selected
	const formInitialValues = selectedDate
		? {
				start_date: selectedDate.toISOString(),
				end_date: new Date(
					selectedDate.getTime() + 60 * 60 * 1000
				).toISOString(),
			}
		: undefined;

	return (
		<Flex flex={1} vertical className={styles.calendarContainer}>
			<CalendarHeader
				calRef={calRef}
				currentDate={currentDate}
				currentView={currentView}
				onViewChange={setCurrentView}
				onDateChange={setCurrentDate}
				onAddEvent={handleAddEvent}
			/>
			<FullCalendar
				plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin]}
				initialView="dayGridMonth"
				headerToolbar={false}
				editable={true}
				selectable={true}
				selectMirror={true}
				dayMaxEvents={true}
				weekends={true}
				height="100%"
				ref={calRef}
				events={calendarEvents}
				datesSet={handleDatesSet}
				select={handleDateSelect}
				eventClick={handleEventClick}
				eventDrop={handleEventChange}
				eventResize={handleEventChange}
			/>

			<Drawer
				title="Add Event"
				placement="right"
				width={700}
				onClose={handleDrawerClose}
				open={drawerOpen}
				extra={
					<Space size={12}>
						<Button
							type="primary"
							danger
							onClick={handleDrawerClose}
							className="actionCardButton"
							icon={<CloseCircleOutlined />}
						>
							Close
						</Button>
						<Button
							type="primary"
							onClick={handleSave}
							className="actionCardButton"
							icon={<SaveOutlined />}
						>
							Save
						</Button>
					</Space>
				}
			>
				{drawerOpen && (
					<FormDraw
						entity="schedule_event"
						formMode="create"
						initialValues={formInitialValues}
						showActions={false}
						onFormReady={handleFormReady}
						onSubmit={async (values) =>
							fetchAPI(buildApiUrl("/schedule-events"), {
								method: "POST",
								body: JSON.stringify(values),
							})
						}
					/>
				)}
			</Drawer>
		</Flex>
	);
};
